import { formatMoney, formatNumber, unformat } from 'accounting-js';
import { isNumber, getOr } from 'lodash/fp';
import { isToday, isTomorrow, isYesterday } from './index';
import { formatDate, formatTime, getDatesDifference } from './datetime';

export const NA = 'N/A';
export const TYPE_CURRENCY = 'currency';
export const TYPE_PERCENT = 'percent';
export const TYPE_RATIO = 'ratio';
export const TYPE_NUMBER = 'number';
export const TYPE_TEXT = 'text';

export type ValueType =
    | typeof TYPE_CURRENCY
    | typeof TYPE_PERCENT
    | typeof TYPE_RATIO
    | typeof TYPE_NUMBER
    | typeof TYPE_TEXT;

export const VALUE_TYPES: ValueType[] = [
    TYPE_CURRENCY,
    TYPE_PERCENT,
    TYPE_NUMBER,
    TYPE_TEXT,
    TYPE_RATIO,
];

type Formatter = (value: number, options?: Intl.NumberFormatOptions) => string;

export const validateStringData = (data: string): string => data || NA;

export const getAdaptedDate = (
    date: Date | undefined,
    t: (key: string) => string,
    withTodayWord?: boolean,
): string => {
    if (date === undefined) return NA;

    if (isToday(date)) return withTodayWord ? t('messages.today') : formatTime(date);
    if (isTomorrow(date)) return t('messages.tomorrow');
    if (isYesterday(date)) return t('messages.yesterday');

    return formatDate(date);
};

export const percentIsZero = (num: number, precision = 2): number | string => {
    if (num === 0) return num;
    if (Number(num).toFixed(precision) === (0).toFixed(precision)) return '<1';
    if (Number(num).toFixed(precision) === `-${(0).toFixed(precision)}`) return '>-1';

    return num;
};

export const chartValueFormatCurrency = {
    symbol: '$',
    precision: 0,
    thousand: ',',
    decimal: '.',
    format: '%v%s',
} as const;

export const chartValueFormatPercent = {
    symbol: '%',
    precision: 2,
    thousand: ',',
    decimal: '.',
    format: '%v%s',
} as const;

export const formatCurrency = (amount: number | string, symbol?: string): string => (isNumber(+amount)
    ? formatMoney(+amount, {
        symbol: symbol || '',
        decimal: '.',
        thousand: ',',
        format: '%s %v',
    })
    : NA);

export const formatCurrencyWithPrecision = (
    amount: number | string,
    precision: number,
    symbol?: string,
): string => (isNumber(+amount)
    ? formatMoney(+amount, {
        precision,
        symbol: symbol || '',
        decimal: '.',
        thousand: ',',
        format: '%s %v',
    })
    : NA);

export const unformatCurrency = (amount: string): number => unformat(amount);

export const formatPercentageValue = (amount: number, precision = 2): string => {
    if (!isNumber(amount)) return NA;
    const number = Math.abs(amount.toFixed(precision) as unknown as number)
        === Number((0).toFixed(precision))
        ? 0
        : amount;

    return formatNumber(number, { precision, thousand: '', decimal: ',' });
};

export const formatPercentage = (
    amount: number | unknown,
    { multiplier } = { multiplier: 1 },
): string => (typeof amount === 'number'
    ? `${formatPercentageValue(amount * multiplier)}%`
    : `${NA} %`);

export const getfromDataObject = <T>(
    data: unknown,
    path: string,
    defaultData: T | string = NA,
): T | string => getOr(defaultData, path, data);

export const formatBigNumber = (value: number | undefined, precision = 2): string => (value === undefined || Number.isNaN(value)
    ? NA
    : formatMoney(value, {
        precision,
        decimal: '.',
        thousand: ',',
        format: '%v',
    }));

const getContractedNumber = (
    num: number,
    value: number,
    formatter: Formatter | undefined,
    precision: number,
): string => (formatter
    ? formatter(value / num, {
        maximumFractionDigits: precision,
        minimumFractionDigits: precision,
    })
    : formatBigNumber(value / num));

export const formatNumberRounding = (
    value: number | undefined,
    formatter?: Formatter,
    precision = 2,
): string => {
    if (value === undefined || Number.isNaN(value)) return NA;

    return formatter ? formatter(value) : formatBigNumber(value);
};

const formatNumberOnlyWithFormatterDecorator = (formatNumberFunction: typeof formatNumberRounding) => (
    value: number | undefined,
    formatter?: Formatter,
    precision = 2,
    useOnlyFormatter = false,
): string => {
    if (useOnlyFormatter && formatter && value !== undefined) {
        return formatter(value, {
            maximumFractionDigits: precision,
            minimumFractionDigits: precision,
        });
    }

    return formatNumberFunction(value, formatter, precision);
};

const decoratedFormatNumberRounding = formatNumberOnlyWithFormatterDecorator(formatNumberRounding);

export const formatSignPercent = (value: number | unknown): string => (isNumber(value) ? `${(value as number) > 0 ? '+' : ''}${formatPercentage(value)}` : `${NA} %`);

export const formatSignPercentWithoutDecimals = (value: number | unknown): string => (isNumber(value)
    ? `${(value as number) > 0 ? '+' : ''}${formatPercentageValue(value as number, 0)}%`
    : `${NA} %`);

export const formatValueByType = (
    value: number,
    typeValue: ValueType,
    withSign: boolean,
    formatter: Formatter,
    precision = 2,
    useOnlyFormatter = false,
): string => {
    const sign = withSign && value > 0 ? '+' : '';

    switch (typeValue) {
        case TYPE_CURRENCY:
            return `${sign}${decoratedFormatNumberRounding(value, formatter, precision, useOnlyFormatter)}`;
        case TYPE_PERCENT:
            return `${sign}${formatter(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}%`;
        case TYPE_RATIO:
            return `${sign}${formatter(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
        case TYPE_NUMBER:
            return `${sign}${formatter(value, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}`;
        default:
            return String(value);
    }
};

export const getColorClass = (value: unknown, className: string, precision = 2): string => {
    if (typeof value !== 'number') return className;

    const number = Number((value).toFixed(precision));

    if (number > 0) return `${className} ${className}_positive`;
    if (number < 0) return `${className} ${className}_negative`;

    return className;
};

export const yAxisFormatTotal = ({ value }: { value: number }): string => formatNumberRounding(value);

export const yAxisFormatPercent = (value: number): string => `${formatNumber(value * 1, { precision: 2, thousand: '', decimal: ',' })}%`;

export const calendarFormat = {
    dayFormat: 'D',
    timeFormat: 'H:mm',
    monthsFormat: 'MMM',
    firstDayFormat: 'MMMM Do',
    weekdaysMinFormat: 'dddd',
} as const;

export const labelFormat = (
    num: number,
    formatter: (n: string) => string = (n) => n,
): string => `<span class="label">${formatter(formatSignPercentWithoutDecimals(num))}</span>`;

export const allocationPercentFormat = (number: number): string => `${percentIsZero(formatBigNumber(number, 1))}%`;

export const allocationSAPercentFormat = (number: number): string => `${percentIsZero(formatBigNumber(number, 1))}`;

export const validateData = (data: unknown): unknown => (data || data === 0 ? data : NA);

export const validateNumber = (data: number | undefined | null): number => data || 0;

export const formatWithDate = (
    num: string | number,
    date: Date,
    getFormattedDate?: (date: string) => string,
): string => `<b>${num}</b><br/>${getFormattedDate ? getFormattedDate(formatDate(date)) : formatDate(date)}`;

export const formatProjectionDate = (date: Date, t: (key: string) => string): string => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const year = getDatesDifference(date, start, 'years');
    const yearText = year > 1 ? t('charts.years') : t('clientDashboard.year');

    return `${year} ${yearText}`;
};

export const formatInput = (value: number | undefined | ''): string => (value !== undefined && value !== '' ? formatBigNumber(value, 0) : '');

export const labelBarFormat = (label: string, value: number): string => {
    const valuesFormatted = formatInput(value);

    return `<label class="Form-control-label"><span>${label}</span></label>${valuesFormatted}`;
};

export const bytesToSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    if (bytes === 0) return 'n/a';

    const i = parseInt(String(Math.floor(Math.log(bytes) / Math.log(1024))), 10);

    if (i === 0) return `${bytes} ${sizes[i]})`;

    return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
};

type AnyObject = Record<string, unknown>;

export const flattenArray = (array: AnyObject[]): AnyObject[] => {
    if (!array?.length) return [];

    const result: AnyObject[] = [];

    function flatten(obj: AnyObject): void {
        const flatObj = { ...obj };

        Object.keys(flatObj).forEach((key) => {
            if (Array.isArray(flatObj[key])) delete flatObj[key];
        });

        result.push(flatObj);

        Object.keys(obj).forEach((key) => {
            if (Array.isArray(obj[key])) {
                (obj[key] as AnyObject[]).forEach((nestedObj) => flatten(nestedObj));
            }
        });
    }

    array.forEach((obj) => flatten(obj));

    return result;
};

export function removeNonUniqueEntries<T>(array: T[] | undefined, length: number): T[] {
    const frequencyMap = array
        ?.filter((item): item is NonNullable<T> => item != null)
        .reduce<Record<string, number>>((map, item) => {
            const key = String(item);

            map[key] = (map[key] || 0) + 1;

            return map;
        }, {});

    return array?.filter((item) => item != null && frequencyMap?.[String(item)] === length) ?? [];
}

export function roundToNearestTenth(num: number | unknown): number | unknown {
    if (Number.isNaN(+(num as number))) return num;

    const rounded = Math.round((+(num as number) + Number.EPSILON) * 10) / 10;

    return +rounded.toFixed(2);
}
